import { createSelector } from '@reduxjs/toolkit';
import { DealStatus, DealType } from 'components/common/types/Deal.types';
import { Store } from 'components/common/types/Store.types';
import { isToday } from 'date-fns';
import {
  getIsAnyLineFetchingAvailability,
  getIsNewAvailabilityFetched,
  getIsValidAvailabilityRequest,
} from 'store/dealManagement/selectors';
import { getDealType } from 'store/dealManagement/selectors/getDealType';
import { getIsNewDeal } from 'store/dealManagement/selectors/getIsNewDeal';
import { isPastDate } from 'utils/isPastDate';

export const getDealSummaryCardVisibility = createSelector(
  (state: Store) => state.dealManagement.commonDeal.bookingStatusCode,
  getIsValidAvailabilityRequest,
  getIsNewAvailabilityFetched,
  getIsAnyLineFetchingAvailability,
  (state: Store) => state.dealManagement.commonDeal.currentLine.isCurrentLineWithProposalAllocation,
  getIsNewDeal,
  getDealType,
  (state: Store) => Boolean(state.dealManagement.commonDeal.currentLine.id),
  (state: Store) => state.dealManagement.commonDeal.currentLine.bookingStatusCode,
  (
    bookingStatusCode,
    isValidAvailabilityRequest,
    isNewAvailabilityFetched,
    isFetchingAvailability,
    isCurrentLineWithProposalAllocation,
    isNewDeal,
    dealType,
    isExistingLine,
    currentLineBookingStatusCode,
  ) => {
    const isGuaranteed = dealType === DealType.GUARANTEED;
    const isNonGuaranteed = dealType !== DealType.GUARANTEED;

    const isNotCancelledOrTerminated = ![DealStatus.CANCELLED, DealStatus.TERMINATED].includes(bookingStatusCode);
    const isApprovedOrLive = [DealStatus.APPROVED, DealStatus.LIVE].includes(bookingStatusCode);
    const isNonGuaranteedWithValidRequest = isNonGuaranteed && isValidAvailabilityRequest;

    const isGuaranteedExistingLineWithNewAvailability =
      isGuaranteed &&
      isValidAvailabilityRequest &&
      isNewAvailabilityFetched &&
      currentLineBookingStatusCode &&
      isNotCancelledOrTerminated &&
      ![DealStatus.CANCELLED, DealStatus.TERMINATED].includes(currentLineBookingStatusCode);

    const isGuaranteedNewLineWithNewAvailability =
      isGuaranteed &&
      isValidAvailabilityRequest &&
      isNewAvailabilityFetched &&
      (isNewDeal || !isExistingLine) &&
      isNotCancelledOrTerminated;

    const isGuaranteedApprovedOrLiveExistingLine =
      isGuaranteed &&
      isValidAvailabilityRequest &&
      isNewAvailabilityFetched &&
      isExistingLine &&
      currentLineBookingStatusCode &&
      isApprovedOrLive &&
      [DealStatus.APPROVED, DealStatus.LIVE].includes(currentLineBookingStatusCode);

    const isAvailabilityCardVisible = Boolean(
      isNonGuaranteedWithValidRequest ||
        isGuaranteedExistingLineWithNewAvailability ||
        isGuaranteedNewLineWithNewAvailability ||
        isGuaranteedApprovedOrLiveExistingLine,
    );

    const isDeliveryCardVisible =
      isGuaranteed && [DealStatus.LIVE, DealStatus.TERMINATED, DealStatus.ENDED].includes(bookingStatusCode);

    const isProposalCardVisible =
      isGuaranteed &&
      ((bookingStatusCode === DealStatus.PENDING_APPROVAL &&
        !isFetchingAvailability &&
        !isNewAvailabilityFetched &&
        isExistingLine) ||
        (isCurrentLineWithProposalAllocation &&
          (isNewDeal ||
            [DealStatus.PENDING_APPROVAL, DealStatus.APPROVED, DealStatus.LIVE].includes(bookingStatusCode))) ||
        bookingStatusCode === DealStatus.CANCELLED);

    const isCheckAllocationButtonVisible =
      isGuaranteed && ![DealStatus.CANCELLED, DealStatus.TERMINATED, DealStatus.ENDED].includes(bookingStatusCode);

    const isAllocationCardVisible =
      isGuaranteed &&
      ((isExistingLine && [DealStatus.APPROVED, DealStatus.LIVE].includes(bookingStatusCode)) ||
        [DealStatus.TERMINATED, DealStatus.ENDED].includes(bookingStatusCode));

    return {
      isAllocationCardVisible,
      isAvailabilityCardVisible,
      isDeliveryCardVisible,
      isProposalCardVisible,
      isCheckAllocationButtonVisible,
    };
  },
);

export const getLineSummaryCardVisibility = createSelector(
  getDealSummaryCardVisibility,
  (state: Store) => Boolean(state.dealManagement.commonDeal.currentLine.id),
  (state: Store) => state.dealManagement.commonDeal.currentLine.startDate,
  (state: Store) => state.dealManagement.commonDeal.currentLine.bookingStatusCode,
  (dealCardsVisibility, isExistingLine, currentDealLineStartDate) => {
    const isCurrentLineStartDateTodayOrPast =
      currentDealLineStartDate !== null &&
      (isPastDate(new Date(currentDealLineStartDate)) || isToday(new Date(currentDealLineStartDate)));

    const { isAvailabilityCardVisible, isProposalCardVisible, isDeliveryCardVisible } = dealCardsVisibility;
    const isLineDeliveryCardVisible = isDeliveryCardVisible && isExistingLine && isCurrentLineStartDateTodayOrPast;

    return {
      isLineProposalCardVisible: isProposalCardVisible,
      isLineDeliveryCardVisible,
      isLineAvailabilityCardVisible: isAvailabilityCardVisible,
    };
  },
);
